import React from "react";

import { Category, Label } from "@/constants/events";
import { Dispensary } from "@/custom-types/Dispensary";
import { DispensaryReview } from "@/custom-types/Reviews";
import { useSelector } from "@/redux/hooks";
import { getEducationalMaterials } from "@/redux/selectors/complianceRules";
import { getCurrentDeliveryZone } from "@/redux/selectors/dispensary";
import { isInMSOTestGroup } from "@/utils/dispensaryUtils";
import { getPaymentMethodsString } from "@/utils/fulfillmentUtils";

import ExpandableContainer from "@/components/botanic/ExpandableContainer";
import PhotoOverview from "@/components/Dispensary/AboutDispensary/PhotoOverview";
import ReviewsOverview from "@/components/Dispensary/AboutDispensary/ReviewsOverview";
import SponsoredVideo from "@/components/Dispensary/AboutDispensary/SponsoredVideo";
import RetailHours from "@/components/Dispensary/RetailHours";
import AwardBadgeIcon from "@/components/Icons/award_badge.svg";
import RetailerDetailsCard from "@/components/RetailerDetailsCard";
import TrackImpression from "@/components/TrackImpression";

import PromotionsSection from "../PromotionsSection";
import EducationalMaterialsStoreFrontImages from "./EducationalMaterials";
import { MSOLink } from "./MSOLink";
import RetailerInfoSection from "./RetailerInfoSection";
import UpdatesSection from "./Updates";

const AboutDispensary: React.FC<{
  dispensary: Dispensary;
  ratingsWithoutContent: number;
  reviews?: DispensaryReview[];
  totalReviews: number;
}> = ({ dispensary, ratingsWithoutContent, reviews, totalReviews }) => {
  const {
    country,
    description,
    featureTier,
    id,
    leaflyListYears,
    name,
    retailType,
    schedules,
    slug,
    sponsoredVideoId,
    timeZone,
    topLevelOrganizationSlug,
    pickupEnabled,
    hasReservationsEnabled,
    hasDeliveryEnabled,
    deliveryEnabled,
    pickupPaymentMethods,
  } = dispensary;
  const showPickup = pickupEnabled || hasReservationsEnabled;
  const showDelivery = hasDeliveryEnabled || deliveryEnabled;
  const showStoreInfo = getPaymentMethodsString(pickupPaymentMethods);
  const deliveryZone = useSelector(getCurrentDeliveryZone);

  const educationalMaterials = useSelector(getEducationalMaterials);

  const isInfoTier = featureTier === "info";

  return (
    <div className="container" id="about-dispensary">
      <h3 className="heading--m mb-md">
        About this {country === "CA" ? "store" : retailType}
      </h3>
      <div className="flex flex-col gap-xxl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-xl">
          <div className="flex flex-col gap-sm">
            {description && (
              <>
                <h4 className="text-xs uppercase">{name}</h4>
                <div className="-mb-lg">
                  <ExpandableContainer initialHeight="216px">
                    <p className="text-sm break-words whitespace-pre-wrap">
                      {description}
                    </p>
                  </ExpandableContainer>
                </div>
              </>
            )}
            {topLevelOrganizationSlug &&
              isInMSOTestGroup(topLevelOrganizationSlug) && (
                <MSOLink slug={topLevelOrganizationSlug} />
              )}
            {!description && <RetailerInfoSection dispensary={dispensary} />}
          </div>
          {description && (
            <div className="flex flex-col gap-sm">
              <RetailerInfoSection dispensary={dispensary} />
            </div>
          )}
          <div className="flex flex-col gap-xl">
            {leaflyListYears.length > 0 && (
              <TrackImpression
                category={Category.dispensaryFooter}
                label={Label.leaflyListAwards}
                customDimensions={{ dispensaryId: id }}
              >
                <h3 className="text-xs uppercase mb-sm">
                  <span>leafly list awards</span>
                </h3>
                <ul className="flex flex-col gap-sm">
                  {leaflyListYears.map((year: number) => (
                    <li className="flex items-center gap-xs" key={year}>
                      <span>
                        <AwardBadgeIcon width={28} height={28} />
                      </span>
                      <span className="font-bold text-xs">
                        {year}&nbsp;Leafly List Winner
                      </span>
                    </li>
                  ))}
                </ul>
              </TrackImpression>
            )}
            <RetailHours
              timeZone={timeZone}
              schedules={schedules}
              className="-mb-sm"
            />
            {showPickup ? (
              <div
                data-testid="show-pickup"
                className="pt-lg pb-sm border-t border-light-grey"
              >
                <RetailerDetailsCard
                  dispensary={dispensary}
                  fulfillmentType="pickup"
                  pickupReadinessLabel={undefined}
                />
              </div>
            ) : null}
            {showDelivery ? (
              <div
                data-testid="show-delivery"
                className="pt-xl border-t border-light-grey"
              >
                <RetailerDetailsCard
                  dispensary={dispensary}
                  deliveryZone={deliveryZone?.deliveryServiceArea}
                  fulfillmentType="delivery"
                />
              </div>
            ) : null}
            {!showDelivery && !showPickup && showStoreInfo && (
              <div
                data-testid="store-payment-info"
                className="pt-xl border-t border-light-grey"
              >
                <RetailerDetailsCard
                  dispensary={dispensary}
                  fulfillmentType="store"
                />
              </div>
            )}
          </div>
        </div>
        {educationalMaterials?.storeFront?.images?.length && (
          <EducationalMaterialsStoreFrontImages
            images={educationalMaterials.storeFront.images}
          />
        )}
        {!isInfoTier && (
          <>
            <PhotoOverview
              initialPhotos={dispensary.photos}
              dispensaryName={dispensary.name}
              dispensarySlug={dispensary.slug}
            />
            <PromotionsSection
              id={id}
              name={name}
              showDealsCta
              slug={slug}
              title={`Promotions at ${name}`}
              timeZone={timeZone}
            />
            <UpdatesSection dispensary={dispensary} />
            <SponsoredVideo sponsoredVideoId={sponsoredVideoId} />
          </>
        )}
        <ReviewsOverview
          dispensary={dispensary}
          ratingsWithoutContent={ratingsWithoutContent}
          reviews={reviews}
          totalReviews={totalReviews}
        />
      </div>
    </div>
  );
};

export default AboutDispensary;
